import { useEffect, useState } from 'react';
import { apiClient, axiosClient, useAuth } from '../../auth-lib';
import { Button, Card, Input, Modal, Select, StatusPill, EmptyState, Skeleton, useToast } from '../../design-system';
import '../styles/Auditors.css';

const MANAGER_LEVEL = 3;

export const AUDIT_TYPES = [
  { value: 'statutory', label: 'Statutory' },
  { value: 'internal', label: 'Internal' },
  { value: 'tax', label: 'Tax' },
  { value: 'other', label: 'Other' },
];

const typeLabel = (t) => AUDIT_TYPES.find((x) => x.value === t)?.label || t;

const EMPTY = {
  name: '',
  affiliation: 'in_house',
  userId: '', // in-house only — the org user this auditor is
  firmName: '',
  membershipNumber: '',
  email: '',
  phone: '',
  types: [],
  limits: { statutory: '', internal: '', tax: '', other: '' },
  notes: '',
};

// One "Tax 55 / 60 · 5 left" chip. Turns amber when the headroom is nearly gone
// and red once the ceiling is breached — the whole point of tracking this.
function CapacityChip({ cap }) {
  const { type, assigned, limit, remaining, isOverLimit } = cap;
  if (limit == null) {
    return (
      <span className="auditor-cap" title="No ceiling set for this type">
        <b>{typeLabel(type)}</b> {assigned} <span className="auditor-cap-muted">/ no cap</span>
      </span>
    );
  }
  const tone = isOverLimit ? 'is-over' : remaining <= Math.max(1, limit * 0.1) ? 'is-tight' : '';
  return (
    <span className={`auditor-cap ${tone}`} title={`${assigned} of ${limit} ${typeLabel(type)} audits assigned`}>
      <b>{typeLabel(type)}</b> {assigned} / {limit}
      <span className="auditor-cap-left">
        {isOverLimit ? `${assigned - limit} over` : `${remaining} left`}
      </span>
    </span>
  );
}

// The org's auditor directory: who can sign audits, in what capacity, and how
// much of each one's statutory ceiling is already used up.
function AuditorsManager() {
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = (user?.role?.level || 0) >= MANAGER_LEVEL;

  const [auditors, setAuditors] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('active'); // 'active' | 'deleted'
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // auditor _id, or null (= add)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isDeletedView = view === 'deleted';

  useEffect(() => {
    fetchAuditors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  useEffect(() => {
    // The firm's own users — an in-house auditor must be one of them.
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
  }, []);

  const fetchAuditors = async () => {
    try {
      setLoading(true);
      const params = isDeletedView ? { deleted: 'true' } : {};
      const res = await apiClient.get('/api/auditors', { params });
      setAuditors(res.data);
    } catch (err) {
      toast.danger('Failed to load auditors');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setError(''); setModalOpen(true); };

  const openEdit = (a) => {
    setEditing(a._id);
    setForm({
      name: a.name || '',
      affiliation: a.affiliation || 'in_house',
      userId: a.userId?._id || a.userId || '',
      firmName: a.firmName || '',
      membershipNumber: a.membershipNumber || '',
      email: a.email || '',
      phone: a.phone || '',
      types: a.types || [],
      limits: AUDIT_TYPES.reduce((acc, t) => {
        acc[t.value] = a.limits?.[t.value] ?? '';
        return acc;
      }, {}),
      notes: a.notes || '',
    });
    setError('');
    setModalOpen(true);
  };

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const toggleType = (t) => {
    setForm((f) => ({
      ...f,
      types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t],
    }));
  };

  const setLimit = (t, value) => {
    setForm((f) => ({ ...f, limits: { ...f.limits, [t]: value } }));
  };

  const save = async (e) => {
    e.preventDefault();
    const inHouse = form.affiliation === 'in_house';
    if (inHouse && !form.userId) { setError('Pick the user this in-house auditor is'); return; }
    if (!inHouse && !form.name.trim()) { setError('Name is required'); return; }
    if (form.types.length === 0) { setError('Pick at least one audit type'); return; }

    setSaving(true);
    setError('');
    try {
      const res = editing
        ? await apiClient.put(`/api/auditors/${editing}`, form)
        : await apiClient.post('/api/auditors', form);
      setAuditors((list) => (editing
        ? list.map((a) => (a._id === editing ? res.data : a))
        : [...list, res.data].sort((a, b) => a.name.localeCompare(b.name))));
      setModalOpen(false);
      toast.success(editing ? 'Auditor updated' : 'Auditor added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save auditor');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      const res = await apiClient.put(`/api/auditors/${a._id}`, { isActive: !a.isActive });
      setAuditors((list) => list.map((x) => (x._id === a._id ? res.data : x)));
    } catch (err) {
      toast.danger('Failed to update auditor');
    }
  };

  // Soft delete: the auditor drops out of this list but is recoverable from the
  // Deleted view — nothing is ever hard-deleted.
  const remove = async (a) => {
    try {
      await apiClient.delete(`/api/auditors/${a._id}`);
      setAuditors((list) => list.filter((x) => x._id !== a._id));
      toast.success('Auditor removed — recoverable from the Deleted view');
    } catch (err) {
      // 409 = still appointed somewhere; the server explains who/where.
      toast.danger(err.response?.data?.error || 'Failed to remove auditor');
    }
  };

  const restore = async (a) => {
    try {
      await apiClient.patch(`/api/auditors/${a._id}/restore`);
      setAuditors((list) => list.filter((x) => x._id !== a._id));
      toast.success('Auditor restored');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to restore auditor');
    }
  };

  return (
    <Card>
      <Card.Header
        title="🧾 Auditors"
        subtitle="Who can sign audits, in what capacity, and how much of their ceiling is used"
        action={
          <div className="auditor-header-actions">
            {canEdit && !isDeletedView && (
              <Button variant="primary" size="sm" onClick={openAdd}>+ Add Auditor</Button>
            )}
            <div className="auditor-toggle">
              <button type="button" className={!isDeletedView ? 'is-active' : ''} onClick={() => setView('active')}>Active</button>
              <button type="button" className={isDeletedView ? 'is-active' : ''} onClick={() => setView('deleted')}>Deleted</button>
            </div>
          </div>
        }
      />

      {loading ? (
        <Skeleton.Text lines={3} />
      ) : auditors.length === 0 ? (
        <EmptyState
          icon={isDeletedView ? '🗑️' : '🧾'}
          title={isDeletedView ? 'No deleted auditors' : 'No auditors yet'}
          description={isDeletedView
            ? 'Removed auditors show up here and can be restored.'
            : "Add the people who sign your audits — one of your own users, or an outside CA — and set each one's audit ceiling."}
          action={canEdit && !isDeletedView ? <Button variant="primary" size="sm" onClick={openAdd}>+ Add Auditor</Button> : null}
        />
      ) : (
        <div className="auditor-list">
          {auditors.map((a) => (
            <div key={a._id} className={`auditor-row ${a.isActive ? '' : 'is-inactive'}`}>
              <div className="auditor-identity">
                <div className="auditor-name-line">
                  <span className="auditor-name">{a.name}</span>
                  <StatusPill
                    variant={a.affiliation === 'external' ? 'info' : 'neutral'}
                    label={a.affiliation === 'external' ? 'External' : 'In-house'}
                  />
                  {!a.isActive && <StatusPill variant="warning" label="Inactive" />}
                </div>
                <div className="auditor-meta">
                  {a.firmName && <span>{a.firmName}</span>}
                  {a.membershipNumber && <span>M. No. {a.membershipNumber}</span>}
                  {a.email && <span>{a.email}</span>}
                  {a.phone && <span>{a.phone}</span>}
                </div>
              </div>

              <div className="auditor-caps">
                {a.capacity?.map((cap) => <CapacityChip key={cap.type} cap={cap} />)}
              </div>

              {canEdit && (
                <div className="auditor-actions">
                  {isDeletedView ? (
                    <Button variant="secondary" size="sm" onClick={() => restore(a)}>Restore</Button>
                  ) : (
                    <>
                      <Button variant="secondary" size="sm" onClick={() => openEdit(a)}>Edit</Button>
                      <Button variant="secondary" size="sm" onClick={() => toggleActive(a)}>
                        {a.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => remove(a)}>Remove</Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <Modal
          isOpen
          onClose={() => setModalOpen(false)}
          title={editing ? 'Edit auditor' : 'Add auditor'}
          footer={
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={save} loading={saving}>Save</Button>
            </>
          }
        >
          <form className="auditor-form" onSubmit={save}>
            <div className="form-row">
              <Select label="Affiliation" name="affiliation" value={form.affiliation} onChange={change}>
                <option value="in_house">In-house (our firm)</option>
                <option value="external">External</option>
              </Select>

              {/* From our firm = one of our users, picked from the list. Typing a
                  name freehand would let the same person exist twice and split
                  their audit count, so it isn't offered. */}
              {form.affiliation === 'in_house' ? (
                <Select label="User" name="userId" value={form.userId} onChange={change} required>
                  <option value="">— Select a user —</option>
                  {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
                </Select>
              ) : (
                <Input label="Name" name="name" value={form.name} onChange={change} required />
              )}
            </div>

            <div className="form-row">
              <Input label="Firm Name" name="firmName" value={form.firmName} onChange={change} placeholder="e.g. Gupta & Co." />
              <Input label="Membership No." name="membershipNumber" value={form.membershipNumber} onChange={change} placeholder="ICAI M. No." />
            </div>

            <div className="form-row">
              <Input label="Email" type="email" name="email" value={form.email} onChange={change} />
              <Input label="Phone" name="phone" value={form.phone} onChange={change} />
            </div>

            {/* Type drives the limit boxes: a ceiling only appears for a capacity
                this person actually holds, because the caps differ per type. */}
            <div className="ds-field">
              <label className="ds-field-label">Audit types & ceilings</label>
              <div className="auditor-type-grid">
                {AUDIT_TYPES.map((t) => {
                  const on = form.types.includes(t.value);
                  return (
                    <div key={t.value} className={`auditor-type-cell ${on ? 'is-on' : ''}`}>
                      <label className="auditor-type-check">
                        <input type="checkbox" checked={on} onChange={() => toggleType(t.value)} />
                        <span>{t.label}</span>
                      </label>
                      <input
                        className="ds-input auditor-limit-input"
                        type="number"
                        min="0"
                        placeholder="No cap"
                        aria-label={`${t.label} audit limit`}
                        disabled={!on}
                        value={form.limits[t.value]}
                        onChange={(e) => setLimit(t.value, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
              <span className="ds-field-helper">
                Max audits this person may hold in each capacity. Leave a box empty for no ceiling.
              </span>
            </div>

            <div className="ds-field">
              <label className="ds-field-label" htmlFor="auditor-notes">Notes</label>
              <textarea
                id="auditor-notes"
                className="ds-input"
                name="notes"
                rows="2"
                value={form.notes}
                onChange={change}
              />
            </div>

            {error && <p className="auditor-form-error">{error}</p>}
          </form>
        </Modal>
      )}
    </Card>
  );
}

export default AuditorsManager;
